import type { TextFieldProps } from '@mui/material';
import { Box, FormLabel, TextField } from '@mui/material';
import { getCountryCallingCode, getExampleNumber } from 'libphonenumber-js';
import examplePhoneNumbers from 'libphonenumber-js/examples.mobile.json';
import React from 'react';
import type { Country } from 'react-phone-number-input';
import ReactPhoneInput from 'react-phone-number-input';

interface PhoneInputProps extends Omit<
  TextFieldProps,
  'onChange' | 'value' | 'ref'
> {
  /**
   * Callback that fires when the input value changes
   * `value` will be the parsed phone number in E.164 format.
   *  Example: "+12133734253".
   */
  onChange: (value: string | undefined) => void;
  /**
   * Value of the component
   */
  value: string | undefined;
  /**
   * ISO 3166-1 alpha-2 country code to seed the phone country/calling code
   * with, e.g. from an address country field. Defaults to CA.
   */
  country?: string;
}

// react-phone-number-input passes unknown props through to inputComponent.
// `label` is stripped here — it's rendered above via FormLabel to avoid MUI
// TextField's outlined variant producing two DOM nodes for the same text
// (InputLabel + the hidden notch <span> inside the fieldset legend).
const TextFieldRef = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof TextField>
>(
  (
    { error, helperText, label: _label, size, required, ...inputProps },
    ref
  ) => (
    <TextField
      inputRef={ref}
      error={error}
      helperText={helperText}
      required={required}
      size={size ?? 'small'}
      {...inputProps}
    />
  )
);

// Generic mask used when libphonenumber-js has no example number for the
// country, built from that country's real calling code so we don't show a
// misleading "+1" prefix for non-North-American countries. Some selectable
// countries/territories (e.g. Antarctica) have no calling code at all, in
// which case `getCountryCallingCode` throws — fall back to a plain mask.
function getFallbackPlaceholder(country: Country): string {
  try {
    return `+${getCountryCallingCode(country)} XXX XXX XXXX`;
  } catch {
    return 'XXX XXX XXXX';
  }
}

// Builds a generic format mask (e.g. "+27 XX XXX XXXX") from a real example
// number, rather than showing a realistic-looking sample number. Keeps the
// "+<calling code>" prefix and all punctuation/spacing, masking only the
// national-number digits with `X`.
function getPlaceholder(country: Country): string {
  const exampleNumber = getExampleNumber(country, examplePhoneNumbers);
  if (!exampleNumber) {
    return getFallbackPlaceholder(country);
  }

  const formatted = exampleNumber.formatInternational();
  const callingCodePrefix = `+${exampleNumber.countryCallingCode}`;
  if (!formatted.startsWith(callingCodePrefix)) {
    return getFallbackPlaceholder(country);
  }

  const nationalPart = formatted.slice(callingCodePrefix.length);
  return `${callingCodePrefix}${nationalPart.replace(/\d/g, 'X')}`;
}

function PhoneInput({
  onChange,
  value,
  label,
  country,
  required,
  fullWidth,
  id,
  error,
  ...props
}: PhoneInputProps) {
  const defaultCountry = (country || 'CA') as Country;
  // The label sits outside any FormControl, so it needs an explicit htmlFor to
  // be associated with the input. Without it the field has no accessible name.
  const generatedId = React.useId();
  const inputId = id ?? generatedId;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {label && (
        // `error` has to be passed explicitly. This label used to sit inside a
        // FormControl and pick the error colour up from its context; the
        // wrapper is a plain Box now so that context is gone. No bottom margin,
        // to keep the label/input spacing the five existing consumers already
        // had.
        <FormLabel
          error={error}
          htmlFor={inputId}
          required={required}
          sx={{ display: 'block' }}
        >
          {label}
        </FormLabel>
      )}
      <ReactPhoneInput
        {...props}
        error={error}
        id={inputId}
        required={required}
        // react-phone-number-input only uses `defaultCountry` to seed the
        // initial render — it won't override a country the widget has
        // already derived. Remounting via `key` when the source-of-truth
        // country changes forces it to pick up the new default, including
        // its calling code and example-number placeholder.
        key={defaultCountry}
        defaultCountry={defaultCountry}
        placeholder={getPlaceholder(defaultCountry)}
        value={value}
        onChange={onChange}
        inputComponent={TextFieldRef}
        fullWidth={fullWidth}
        style={fullWidth ? { width: '100%' } : undefined}
      />
    </Box>
  );
}

export default PhoneInput;
